/**
 * P0-23: 新 UI 引导入口。
 *
 * 导出 mountIsland/unmountIsland（createRoot 包装，root 登记在 Map 便于卸载），
 * 并注册全局 window.KGNewUI，供 index.html 在 initGame() 完成后挂载各岛（P0-25）。
 *
 * 注意：本 bundle 内的 React 18 是模块作用域，与旧 UI 的全局 React 0.13
 * （lib/react.min.js）完全隔离，互不感知。
 */
import { createElement, type ComponentType } from "react";
import { createRoot, type Root } from "react-dom/client";
import "./styles/tailwind.css";
import "./styles/layout.css";
import HelloIsland from "./panels/HelloIsland";
import LeftPanel from "./panels/left/LeftPanel";
import TabNav from "./tabs/TabNav";
import MidPanel from "./tabs/MidPanel";
import BonfireTab from "./tabs/bonfire/BonfireTab";
import { TooltipProvider } from "./components/Tooltip/TooltipProvider";
import VillageTab from "./tabs/village/VillageTab";
import ScienceTab from "./tabs/science/ScienceTab";
import WorkshopTab from "./tabs/workshop/WorkshopTab";
import TradeTab from "./tabs/trade/TradeTab";
import ReligionTab from "./tabs/religion/ReligionTab";
import SpaceTab from "./tabs/space/SpaceTab";
import StatsTab from "./tabs/stats/StatsTab";
import AchievementsTab from "./tabs/achievements/AchievementsTab";
import ChallengesTab from "./tabs/challenges/ChallengesTab";
import TimeTab from "./tabs/time/TimeTab";
import RightPanel from "./panels/right/RightPanel";
import DialogHost, { type DialogApi } from "./dialogs/DialogHost";
import TopBar from "./panels/top/TopBar";
import LoadingScreen from "./panels/loading/LoadingScreen";
import FooterBar from "./panels/footer/FooterBar";
import {
  showNewVersion,
} from "./bridge/GameBridge";
import {
  showAutosaveToast,
  showSaveToast,
} from "./bridge/toasts";
import {
  setLoadingLoaded,
  setLoadingProgress,
} from "./bridge/loading";
import { disableTabKeys, enableTabKeys, isTabKeysActive } from "./tabs/tabKeys";
import {
  ensureMidPanelViewport,
  isTabMigrated,
  registerMigratedTab,
} from "./tabs/midPartition";
import { assertReactMountSafe } from "./bridge/GameBridge";

// P3-11：Bonfire tab 原子切换——登记到 MIGRATED_TABS（midPartition），
// 激活 Bonfire 时 #gameContainerId 隐藏、React 分区显示（TabNav + BonfireTab）。
// 回滚：删除本行 + 移除 index.html 的 mountMidPanel 调用即可（旧 dojo 渲染
// 路径原样保留，显隐由 syncMidPanelVisibility 数据驱动）。
registerMigratedTab("Bonfire", BonfireTab);

// P3-16：Village tab 原子切换。
registerMigratedTab("Village", VillageTab);

// P3-21：Science tab 原子切换。
registerMigratedTab("Science", ScienceTab);

// P3-25：Workshop tab 原子切换。
registerMigratedTab("Workshop", WorkshopTab);

// P3-29：Trade tab 原子切换。
registerMigratedTab("Trade", TradeTab);

// P3-33：Religion tab 原子切换。
registerMigratedTab("Religion", ReligionTab);

// P3-37：Space tab 原子切换。
registerMigratedTab("Space", SpaceTab);

// P3-38：Stats tab 原子切换。
registerMigratedTab("Stats", StatsTab);

// P3-40：Achievements tab 原子切换。
registerMigratedTab("Achievements", AchievementsTab);

// P3-42：Challenges tab 原子切换。
registerMigratedTab("Challenges", ChallengesTab);

// P3-38：Time tab 原子切换。
registerMigratedTab("Time", TimeTab);

export interface KGNewUIApi {
  mountIsland(
    el: Element,
    Component: ComponentType<Record<string, unknown>>,
    props?: Record<string, unknown>,
  ): () => void;
  unmountIsland(el: Element): void;
  /**
   * P3-05：中栏共存分区挂载入口。创建/取用 #kgMidPanelViewport
   * （#gameContainerId 兄弟节点）并挂载 MidPanel；容器未就绪返回 null。
   * 本阶段未启用（index.html 不调用，MIGRATED_TABS 为空）。
   */
  mountMidPanel(): (() => void) | null;
  /** P3-46：旧 {@link js/ui.js updateTabs} 跳过已迁移 tab 的 guard */
  isTabMigrated(tabId: string): boolean;
  panels: Record<string, ComponentType<Record<string, unknown>>>;
  /** P4-13：全局对话框命令式 API */
  dialogs: DialogApi;
  /** P5-01：全局 toast 触发（autosave / save）与新版本提示 */
  toasts: {
    showAutosave(): void;
    showSave(): void;
    showNewVersion(): void;
  };
  /** P5-02：加载进度命令式 API，index.html 模块加载器调用 */
  loading: {
    setProgress(progress: number, message: string): void;
    setLoaded(): void;
  };
  /** P3-04：键盘切 tab（数字键/方向键）新层开关；旧层分支据 isActive() 守卫跳过 */
  tabKeys: {
    enable(): void;
    disable(): void;
    isActive(): boolean;
  };
}

const roots = new Map<Element, Root>();

/**
 * 在既有 DOM 锚点上挂载一个 React 岛。返回卸载函数。
 * 同一元素重复挂载前先卸载旧 root，防止双 root。
 */
export function mountIsland(
  el: Element,
  Component: ComponentType<Record<string, unknown>>,
  props?: Record<string, unknown>,
): () => void {
  // P3-05：防御性断言——React root 不挂在会被 dojo.empty 清掉的节点内
  //（#gameContainerId 及其子树，js/ui.js:384）
  assertReactMountSafe(el);
  unmountIsland(el);
  const root = createRoot(el);
  root.render(createElement(Component, props));
  roots.set(el, root);
  return () => unmountIsland(el);
}

export function unmountIsland(el: Element): void {
  const root = roots.get(el);
  if (root) {
    roots.delete(el);
    root.unmount();
  }
}

function mountMidPanel(): (() => void) | null {
  const viewport = ensureMidPanelViewport();
  if (!viewport) {
    console.warn("[KGNewUI] #gameContainerId 未就绪，跳过 MidPanel 挂载");
    return null;
  }
  return mountIsland(
    viewport,
    () => createElement(TooltipProvider, null, createElement(MidPanel)),
  );
}

declare global {
  interface Window {
    KGNewUI?: KGNewUIApi;
  }
}

window.KGNewUI = {
  mountIsland,
  unmountIsland,
  mountMidPanel,
  isTabMigrated,
  panels: {
    HelloIsland,
    // P2-20 原子切换：组装左栏整体挂载到 #leftColumnViewport（index.html initGame）。
    // P2-04~13 的四个独立面板入口（LeftResourceTable/LeftPins/LeftCraftTable/
    // LeftFastActions）已由 LeftPanel 取代，不再注册。
    Left: LeftPanel,
    // P3-03：tab 导航条已注册未启用（同 P2 各面板做法）；启用由 P3-05
    // MidPanel 共存容器统一承担（随各 tab 迁移进度挂载，见 tabs/TabNav.tsx 文件头）。
    TabNav,
    // P3-05：中栏共存分区外壳（TabNav + 已迁移 tab 内容），已注册未启用；
    // 挂载请用 mountMidPanel()（自建兄弟锚点），不要直接 mountIsland 到
    // #gameContainerId 内（assertReactMountSafe 会抛错）。
    Mid: MidPanel,
    // P5-01：顶部栏根组件，由 P5-01b 在 index.html 中挂载到 #topBar。
    TopBar,
    // P5-02：加载画面根组件，由 index.html 中挂载到 #loadingContainer。
    LoadingScreen,
    // P5-03：底部链接与 toggleCenter 根组件，分别挂载到 #footerLinks 与 #toggleCenter。
    FooterBar,
    // P4-13：右栏根组件，由 P4-15 在 index.html 中挂载到 #rightColumnViewport。
    Right: RightPanel,
    // P4-13：全局 DialogProvider 宿主，挂载到 #dialogViewport。
    DialogHost,
  },
  // P4-13：DialogHost 挂载后会填充实际的 dialogs API；此处先置空对象占位。
  dialogs: {} as DialogApi,
  // P5-01：全局 toast 触发；TopBar 内通过 setToastListener 订阅显示。
  toasts: {
    showAutosave: showAutosaveToast,
    showSave: showSaveToast,
    showNewVersion,
  },
  // P5-02：加载进度命令式 API；LoadingScreen 内通过 setLoading*Listener 订阅。
  loading: {
    setProgress: setLoadingProgress,
    setLoaded: setLoadingLoaded,
  },
  // P3-04：数字键/方向键切 tab 平移到新层；旧层（js/ui.js keyup）两个分支
  // 经 isActive() 守卫跳过，保证单一绑定。index.html initGame 末尾 enable()。
  tabKeys: {
    enable: enableTabKeys,
    disable: disableTabKeys,
    isActive: isTabKeysActive,
  },
};
